import { normaliseWebsite } from '@crm/contracts';
import { useState } from 'react';
import { WebsiteHint } from './company-dialog.tsx';
import { describeError } from '../../lib/api.ts';
import { api, type CompanyPatchBody, type CompanyView } from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';

const TEXT_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'website', label: 'Website' },
  { key: 'industry', label: 'Industry' },
] as const;

type Draft = { name: string; website: string; industry: string; ownerId: string | null };

const draftOf = (c: CompanyView): Draft => ({
  name: c.name,
  website: c.website ?? '',
  industry: c.industry ?? '',
  ownerId: c.ownerId,
});

/**
 * One company in the Companies side panel: a form saved with one PATCH of the changed fields.
 * Name and owner are required; website and industry clear to null.
 */
export function CompanyDetail({
  company,
  onSaved,
}: {
  company: CompanyView;
  onSaved: () => void | Promise<void>;
}) {
  const session = useSession();
  const { members, nameOf } = useMembers();
  const [draft, setDraft] = useState(() => draftOf(company));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canEdit = session.can('customer.update', { ownerId: company.ownerId });

  const values = {
    name: draft.name.trim(),
    website: draft.website.trim() || null,
    industry: draft.industry.trim() || null,
    ownerId: draft.ownerId,
  };
  const changes: CompanyPatchBody = Object.fromEntries(
    Object.entries(values).filter(([k, v]) => v !== company[k as keyof typeof values]),
  );
  const dirty = Object.keys(changes).length > 0;
  // Older rows can hold free text; only a website being changed must be one.
  const badWebsite = 'website' in changes && !!values.website && !normaliseWebsite(values.website);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await api.companies.patch(company.id, changes);
      setDraft(draftOf(saved));
      setError(null);
      await onSaved();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="company-detail">
      <h2 className="text-2xl font-semibold tracking-tight break-words">{company.name}</h2>
      {canEdit ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty && values.name && !badWebsite) void save();
          }}
        >
          {TEXT_FIELDS.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <label className="flex flex-col gap-1 text-sm">
                {f.label}
                <Input
                  value={draft[f.key]}
                  onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                  {...(f.key === 'website' && {
                    placeholder: 'example.com',
                    'aria-invalid': badWebsite,
                  })}
                />
              </label>
              {f.key === 'website' && badWebsite && <WebsiteHint />}
            </div>
          ))}
          <label className="flex flex-col gap-1 text-sm">
            Owner
            <Select
              value={draft.ownerId ?? ''}
              onValueChange={(v) => v && setDraft({ ...draft, ownerId: v })}
            >
              <SelectTrigger aria-label="Company owner">
                <SelectValue placeholder="No owner" />
              </SelectTrigger>
              <SelectContent>
                {members
                  .filter((m) => m.status === 'active' || m.id === company.ownerId)
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </label>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            {dirty && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDraft(draftOf(company));
                  setError(null);
                }}
              >
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!dirty || !values.name || badWebsite || saving}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <dl className="flex flex-col gap-3 text-sm">
          {TEXT_FIELDS.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <dt className="text-muted-foreground text-xs">{f.label}</dt>
              <dd className="break-words">{company[f.key] ?? '—'}</dd>
            </div>
          ))}
          <div className="flex flex-col gap-1">
            <dt className="text-muted-foreground text-xs">Owner</dt>
            <dd>{nameOf(company.ownerId)}</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
